import {
  Document,
  Page,
  View,
  Text,
  Svg,
  Path,
  StyleSheet,
} from "@react-pdf/renderer";

/* ------------------------------------------------------------------ */
/* Styles (react-pdf supports flexbox only: no grid, no rowSpan)       */
/* ------------------------------------------------------------------ */
const BLUE = "#0068b3";
const CYAN = "#35b4e8";
const TINT = "#eaf4fa";
const LINE = "#d5dde3";

const s = StyleSheet.create({
  page: {
    backgroundColor: "#fff",
    paddingTop: 34,
    paddingHorizontal: 38,
    paddingBottom: 70,
    fontFamily: "Helvetica",
    fontSize: 9,
    color: "#222",
  },

  /* header */
  header: { flexDirection: "row", justifyContent: "space-between" },
  hsRow: { flexDirection: "row", alignItems: "center" },
  hsText: { fontFamily: "Times-Roman", fontSize: 7, marginLeft: 3, letterSpacing: 0.4 },
  thrive: { fontFamily: "Helvetica-Bold", fontSize: 44, color: CYAN, letterSpacing: -2, marginTop: 4 },
  title: { fontFamily: "Helvetica-Bold", fontSize: 20, color: BLUE, marginTop: 8 },
  meta: { marginTop: 20 },
  metaRow: { flexDirection: "row", alignItems: "center", marginBottom: 8 },
  metaLabel: { width: 118, textAlign: "right", fontSize: 10.5, color: "#333", marginRight: 12 },
  metaValue: { fontSize: 10.5, color: "#444", width: 150 },
  select: {
    width: 150,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#ccc",
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  selectText: { fontSize: 7.5, color: "#444" },

  /* cards */
  card: { marginTop: 20, borderWidth: 1, borderColor: LINE },
  caption: { backgroundColor: BLUE, paddingVertical: 8 },
  captionText: { fontFamily: "Helvetica-Bold", fontSize: 14, color: "#fff", textAlign: "center" },

  subhead: { flexDirection: "row", backgroundColor: CYAN },
  subCell: { paddingVertical: 5, paddingHorizontal: 8, borderRightWidth: 1, borderRightColor: "#fff" },
  subText: { fontSize: 11.5, color: "#fff" },

  /* column widths shared by all rows */
  c1: { flex: 25.5 },
  c2: { flex: 44 },
  c3: { flex: 30.5 },

  row: { flexDirection: "row", minHeight: 26, borderBottomWidth: 1, borderBottomColor: LINE },
  labelCell: { justifyContent: "center", paddingHorizontal: 8, paddingVertical: 5 },
  labelText: { fontSize: 9, color: "#666" },
  labelSmall: { fontFamily: "Helvetica-Oblique", fontSize: 6.5, color: "#666" },
  tintCell: {
    backgroundColor: TINT,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderLeftWidth: 1,
    borderLeftColor: LINE,
  },
  tintText: { fontSize: 8.5, color: "#555", textAlign: "center" },
  tintBig: { fontSize: 11, color: "#222" },
  valCell: {
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 4,
    borderLeftWidth: 1,
    borderLeftColor: LINE,
  },
  valText: { fontSize: 11.5, color: "#222" },
  bold: { fontFamily: "Helvetica-Bold", color: "#222" },

  /* grouped rows (replacement for rowSpan) */
  group: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: LINE },
  groupRows: { flex: 74.5 },
  innerRow: { flexDirection: "row", minHeight: 26 },
  innerRowBorder: { borderTopWidth: 1, borderTopColor: LINE },

  total: { flexDirection: "row", backgroundColor: CYAN, minHeight: 28, alignItems: "center" },
  totalLabel: { flex: 69.5, paddingHorizontal: 8 },
  totalLabelText: { fontFamily: "Helvetica-Bold", fontSize: 12, color: "#fff" },
  totalValue: { flex: 30.5, alignItems: "center" },
  totalValueText: { fontFamily: "Helvetica-Bold", fontSize: 11.5, color: "#fff" },

  /* footer */
  footer: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-end", marginTop: 18 },
  notes: { width: 340 },
  noteText: { fontSize: 6.5, lineHeight: 1.5, color: "#444", marginBottom: 2 },
  logos: { flexDirection: "row" },
  logo: { marginLeft: 12 },
  logoTop: { fontFamily: "Times-Roman", fontSize: 4.5 },
  logoWord: { fontFamily: "Helvetica-Bold", fontSize: 20, color: CYAN, letterSpacing: -1, lineHeight: 1 },
  logoSub: { fontSize: 6, textAlign: "right" },

  bar: { position: "absolute", left: 0, right: 0, bottom: 0, height: 16, backgroundColor: "#24539a" },
});

/* ------------------------------------------------------------------ */
/* Pieces                                                              */
/* ------------------------------------------------------------------ */
const HenryScheinIcon = ({ size = 10 }) => (
  <Svg width={size} height={size * (26 / 27)} viewBox="0 0 27 26">
    <Path fill="#E92129" d="M0 0H27C22 1.5 16 4 12 7S6 15 0 19Z" />
    <Path fill="#0971B5" d="M27 6V26H0C5 24 12 19 17 12S24 7 27 6Z" />
  </Svg>
);

const Chevron = () => (
  <Svg width={7} height={5} viewBox="0 0 10 6">
    <Path d="M1 1l4 4 4-4" stroke="#666" strokeWidth={1.4} fill="none" />
  </Svg>
);

const Meta = ({ label, children }) => (
  <View style={s.metaRow}>
    <Text style={s.metaLabel}>{label}</Text>
    {children}
  </View>
);

function PointsCard({ memberSince, points }) {
  const rows = [
    { label: "Current Points Balance", ...points?.balance },
    { label: "Redeemed Points", small: "(Last 12 Months)", ...points?.redeemed },
  ];
  return (
    <View style={s.card} wrap={false}>
      <View style={s.caption}>
        <Text style={s.captionText}>Thrive Rewards Points Summary</Text>
      </View>
      <View style={s.subhead}>
        <View style={[s.subCell, s.c1]}><Text style={s.subText}>Member Since: {memberSince}</Text></View>
        <View style={[s.subCell, s.c2, { alignItems: "center" }]}><Text style={s.subText}>Points*</Text></View>
        <View style={[s.subCell, s.c3, { alignItems: "center", borderRightWidth: 0 }]}><Text style={s.subText}>Value</Text></View>
      </View>
      {rows.map((r, i) => (
        <View key={r.label} style={[s.row, i === rows.length - 1 && { borderBottomWidth: 0 }]}>
          <View style={[s.labelCell, s.c1]}>
            <Text style={s.labelText}>{r.label}</Text>
            {r.small && <Text style={s.labelSmall}>{r.small}</Text>}
          </View>
          <View style={[s.tintCell, s.c2]}><Text style={s.tintBig}>{r.points.toLocaleString("en-US")}</Text></View>
          <View style={[s.valCell, s.c3]}><Text style={s.valText}>{`$${r.value.toLocaleString("en-US")}`}</Text></View>
        </View>
      ))}
    </View>
  );
}

function BenefitGroup({ group }) {
  return (
    <View style={s.group} wrap={false}>
      <View style={[s.labelCell, s.c1]}>
        <Text style={s.labelText}>{group.label}</Text>
        {group.small && <Text style={s.labelSmall}>{group.small}</Text>}
      </View>
      <View style={s.groupRows}>
        {group.rows.map((r, i) => (
          <View key={i} style={[s.innerRow, i > 0 && s.innerRowBorder]}>
            <View style={[s.tintCell, { flex: 44 }]}>
              <Text style={s.tintText}>
                {r.text}
                {r.retail ? " " : ""}
                {r.retail && <Text style={s.bold}>{r.retail}</Text>}
                {r.note ? `\n${r.note}` : ""}
              </Text>
            </View>
            <View style={[s.valCell, { flex: 30.5 }]}>
              <Text style={s.valText}>{`$${r.value.toLocaleString("en-US")}` || `$0`}</Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}

function BenefitsCard({ signatureSince, benefits, total }) {
  return (
    <View style={s.card} wrap={false}>
      <View style={s.caption}>
        <Text style={s.captionText}>Thrive Signature Membership Benefits</Text>
      </View>
      <View style={s.subhead}>
        <View style={[s.subCell, s.c1]}><Text style={s.subText}>Member Since: {signatureSince}</Text></View>
        <View style={[s.subCell, s.c2, { alignItems: "center" }]}><Text style={s.subText}>Description</Text></View>
        <View style={[s.subCell, s.c3, { alignItems: "center", borderRightWidth: 0 }]}><Text style={s.subText}>Value</Text></View>
      </View>

      {benefits.map((g) => (
        <BenefitGroup key={g.label} group={g} />
      ))}

      <View style={s.total}>
        <View style={s.totalLabel}><Text style={s.totalLabelText}>Total Savings Value**</Text></View>
        <View style={s.totalValue}><Text style={s.totalValueText}>${total.toLocaleString("en-US")}</Text></View>
      </View>
    </View>
  );
}

const FooterLogo = ({ sub, subColor }) => (
  <View style={s.logo}>
    <Text style={s.logoTop}>HENRY SCHEIN</Text>
    <Text style={s.logoWord}>thrive</Text>
    <Text style={[s.logoSub, { color: subColor }]}>{sub}</Text>
  </View>
);

/* ------------------------------------------------------------------ */
/* Document                                                            */
/* ------------------------------------------------------------------ */
export default function BenefitStatementPDF({ data }) {
  const d = { ...data };

  return (
    <Document title="Thrive Benefit Statement" author="Henry Schein" subject={`Benefit Statement – ${d.customerName}`}>
      <Page size="LETTER" style={s.page}>
        {/* Header */}
        <View style={s.header}>
          <View>
            <View style={s.hsRow}>
              <HenryScheinIcon />
              <Text style={s.hsText}>HENRY SCHEIN</Text>
            </View>
            <Text style={s.thrive}>thrive</Text>
            <Text style={s.title}>Benefit Statement</Text>
          </View>

          <View style={s.meta}>
            <Meta label="Date:"><Text style={s.metaValue}>{d.date}</Text></Meta>
            <Meta label="Customer Name:">
              <View style={s.select}>
                <Text style={s.selectText}>{d.customerName}</Text>
                <Chevron />
              </View>
            </Meta>
            <Meta label="Field Sales Consultant:"><Text style={s.metaValue}>{d.consultant}</Text></Meta>
          </View>
        </View>

        <PointsCard memberSince={d.memberSince} points={d?.points} />
        <BenefitsCard signatureSince={d.signatureSince} benefits={d.benefits} total={d.total} />

        {/* Footnotes + logos */}
        <View style={s.footer} wrap={false}>
          <View style={s.notes}>
            <Text style={s.noteText}>
              * Points Balance and Points Redeemed are linked to your Bill To Account. Points are available for
              redemption according to program terms. For details, check your Thrive Rewards account at
              www.henryscheindental.com/ThriveRewards
            </Text>
            <Text style={s.noteText}>**Points may already be counted as part of Thrive Rewards Current Points Balance</Text>
            <Text style={s.noteText}>
              Thrive Signature Benefits earnings/savings are based on estimates over a rolling 12-month period.
              Actual earnings/savings may vary.
            </Text>
          </View>
          <View style={s.logos}>
            <FooterLogo sub="Rewards" subColor="#d6212f" />
            <FooterLogo sub="Signature" subColor={CYAN} />
          </View>
        </View>

        {/* Bottom bar on every page */}
        <View style={s.bar} fixed />
      </Page>
    </Document>
  );
}
